import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { useNotificationPreferences, useUpdatePreferences } from "../../hooks/useNotifications";
import { Loader2, Bell, Mail, ShieldAlert, X } from "lucide-react";
import { trackEvent } from "../../utils/analytics";

export default function NotificationSettings() {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useNotificationPreferences(true);
  const { mutate: updatePrefs, isPending: isSaving } = useUpdatePreferences();

  const { control, watch, reset } = useForm({
    defaultValues: {
      likes: true,
      saves: true,
      copies: true,
      followers: true,
      comments: true,
      approvals: true,
      badges: true,
      credits: true,
      emailApprovals: true,
      emailFirstCredits: true,
    }
  });

  // When data loads, reset form with fetched preferences
  useEffect(() => {
    if (data?.preferences) {
      reset(data.preferences);
    }
  }, [data, reset]);

  // Watch all values to trigger auto-save
  const currentValues = watch();

  useEffect(() => {
    // Skip saving if data hasn't loaded yet to avoid saving defaults over fetched data
    if (!data?.preferences) return;

    // Check if anything actually changed from what we got from server
    const changedKeys = Object.keys(currentValues).filter(
      (key) => currentValues[key] !== data.preferences[key]
    );

    if (changedKeys.length > 0) {
      const timeoutId = setTimeout(() => {
        trackEvent("notification_settings_updated", { changed_keys: changedKeys.join(",") });
        updatePrefs(currentValues);
      }, 500); // 500ms debounce
      return () => clearTimeout(timeoutId);
    }
  }, [currentValues, data, updatePrefs]);

  const Toggle = ({ name, label, description }) => (
    <div className="flex items-center justify-between py-4 border-b border-gray-100 last:border-0">
      <div className="pr-4">
        <p className="font-semibold text-gray-900">{label}</p>
        <p className="text-sm text-gray-500 mt-0.5">{description}</p>
      </div>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <button
            type="button"
            role="switch"
            aria-checked={field.value}
            onClick={() => field.onChange(!field.value)}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-2 ${
              field.value ? "bg-indigo-600" : "bg-gray-200"
            }`}
          >
            <span className="sr-only">Toggle {label}</span>
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                field.value ? "translate-x-5" : "translate-x-0"
              }`}
            />
          </button>
        )}
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-20">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Notification Settings</h1>
            <p className="text-gray-500 mt-2">Manage how and when you receive notifications.</p>
          </div>
          {/* This is a full page reached from several places (the
              notifications dropdown's gear icon, deep links, etc.) with no
              other way to dismiss it on mobile — the desktop navbar's own
              navigation is enough there, so this only shows on mobile. */}
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Close"
            className="sm:hidden shrink-0 p-2 -mr-2 -mt-1 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors cursor-pointer"
          >
            <X size={20} />
          </button>
        </div>

        {isLoading ? (
          <div className="bg-white rounded-2xl p-16 flex flex-col justify-center items-center text-gray-400 shadow-sm border border-gray-100">
            <Loader2 className="w-8 h-8 animate-spin mb-4 text-indigo-500" />
            <p>Loading your preferences...</p>
          </div>
        ) : isError ? (
          <div className="bg-red-50 p-6 rounded-xl border border-red-100 flex items-start gap-4">
            <ShieldAlert className="text-red-500 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-800">Failed to load preferences</h3>
              <p className="text-red-700 text-sm mt-1">Please refresh the page to try again.</p>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            
            {/* Auto Save Indicator */}
            <div className="flex justify-end h-6">
              {isSaving && (
                <span className="text-sm font-medium text-gray-400 flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin" /> Saving changes...
                </span>
              )}
            </div>

            {/* In-App Notifications */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-6 py-5 border-b border-gray-100 bg-gray-50/50 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center">
                  <Bell size={16} />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">In-App Notifications</h2>
                  <p className="text-sm text-gray-500">Alerts you receive while using Romaya.</p>
                </div>
              </div>
              <div className="px-6">
                <Toggle name="likes" label="Likes" description="Someone likes your published trip." />
                <Toggle name="saves" label="Saves" description="Someone saves your published trip." />
                <Toggle name="copies" label="Copies" description="Someone copies your itinerary." />
                <Toggle name="comments" label="Comments" description="Someone comments on your trip." />
                <Toggle name="followers" label="New Followers" description="Someone follows your profile." />
                {/* Posts go live without approval since Round 12, but rejection and
                    unpublish remain as post-hoc admin actions — so this channel stays,
                    reworded. The `approvals` key is the stored preference; don't rename it. */}
                <Toggle name="approvals" label="Post Status" description="Your published trip is taken down or reinstated." />
                <Toggle name="badges" label="Badges Earned" description="You cross a threshold and earn a new badge." />
                <Toggle name="credits" label="RoamPoints Earned" description="You receive RoamPoints." />
              </div>
            </div>

            {/* Email Notifications */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-6 py-5 border-b border-gray-100 bg-gray-50/50 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center">
                  <Mail size={16} />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Email Notifications</h2>
                  <p className="text-sm text-gray-500">Updates sent directly to your inbox.</p>
                </div>
              </div>
              <div className="px-6">
                <Toggle name="emailApprovals" label="Post Status Updates" description="Get an email if your published trip is taken down or reinstated." />
                <Toggle name="emailFirstCredits" label="First-time RoamPoints" description="Welcome email when you earn your very first RoamPoints." />
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
}
